import React from 'react';
import { X, Heart, ShoppingCart, ArrowRight, Trash2 } from 'lucide-react';
import { Product } from '../types';

interface WishlistDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  wishlistItems: Product[];
  onToggleWishlist: (product: Product) => void;
  onAddToCart: (product: Product) => void;
  onViewProduct: (product: Product) => void;
}

export const WishlistDrawer: React.FC<WishlistDrawerProps> = ({
  isOpen,
  onClose,
  wishlistItems,
  onToggleWishlist,
  onAddToCart,
  onViewProduct
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-turmeric-50">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-turmeric-600 fill-current" />
            <h2 className="font-heading text-xl font-bold text-herbal-900">My Wishlist ({wishlistItems.length})</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 custom-scrollbar">
          {wishlistItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-50">
              <Heart className="h-16 w-16 text-gray-300" />
              <p className="text-lg font-medium text-gray-500">Your wishlist is empty</p>
              <button onClick={onClose} className="text-herbal-800 font-bold hover:underline">Browse Remedies</button>
            </div>
          ) : (
            wishlistItems.map(item => (
              <div key={item.id} className="flex gap-4 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow group">
                <div 
                  className="h-24 w-24 bg-gray-50 rounded-xl p-2 flex-shrink-0 cursor-pointer overflow-hidden"
                  onClick={() => onViewProduct(item)}
                >
                  <img src={item.image} alt={item.name} className="w-full h-full object-contain transform group-hover:scale-110 transition-transform" />
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start">
                      <h3 
                        className="font-bold text-gray-800 line-clamp-1 cursor-pointer hover:text-herbal-800"
                        onClick={() => onViewProduct(item)}
                      >
                        {item.name}
                      </h3>
                      <button 
                        onClick={() => onToggleWishlist(item)}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                        title="Remove from wishlist"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-turmeric-600 font-bold uppercase tracking-wide mt-0.5">{item.category}</p>
                    <p className="text-[10px] text-gray-500 line-clamp-1 mt-1">{item.purpose}</p>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-bold text-herbal-900">
                      {new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(item.price || 0)}
                    </span>
                    <div className="flex gap-2">
                       <button 
                        onClick={() => {
                          onAddToCart(item);
                          onToggleWishlist(item); // Optional: remove from wishlist when adding to cart?
                        }}
                        className="p-2 bg-herbal-800 text-white rounded-lg hover:bg-herbal-900 transition-colors"
                        title="Add to cart"
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => onViewProduct(item)}
                        className="p-2 bg-earth-100 text-herbal-800 rounded-lg hover:bg-earth-200 transition-colors"
                        title="View details"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {wishlistItems.length > 0 && (
          <div className="p-5 border-t border-gray-100 bg-earth-50">
            <button 
              onClick={onClose}
              className="w-full bg-herbal-800 hover:bg-herbal-900 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
};